import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { roleAtLeast, type Role } from '@lms/shared-types'
import { listEmployeesPage } from '../../api/employees'
import { listTeams } from '../../api/teams'
import { useDebounce } from '../../lib/use-debounce'
import { roleLabel } from '../../lib/role'
import { displayName } from '../../lib/display-name'
import {
  useEffectiveEmployeeState,
  type ImpersonatedEmployee,
} from './effective-employee'

/**
 * Compact "Viewing As" control rendered inline inside the TopBar (left of the
 * user identity block). Visible only when:
 *
 *   - the real role is HR / director / exec_director, AND
 *   - the `viewingAs` feature flag is ON (gate handled by AppShell so this
 *     component itself stays unaware).
 *
 * Collapsed: pill reading "Viewing as: yourself". Click expands an employee
 * combobox. Selecting fills the pill with the chosen employee's identity and
 * surfaces an "Exit" chip beside it.
 *
 * Mutations from anywhere in the app still travel under the real session;
 * pages opt-in to the impersonated id by calling `useEffectiveEmployeeId`.
 */

export interface ViewingAsBarProps {
  realRole: Role
}

export function ViewingAsBar({ realRole }: ViewingAsBarProps): JSX.Element | null {
  // Hard role gate. The flag check is done by AppShell so component testing
  // doesn't have to mock import.meta.env.
  if (!roleAtLeast(realRole, 'hr')) {
    return null
  }

  const { impersonated, setImpersonated, clear } = useEffectiveEmployeeState()
  const [open, setOpen] = useState(false)

  const isViewingSelf = impersonated == null
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Click-outside closes the picker.
  useEffect(() => {
    if (!open) return
    function onDocClick(event: MouseEvent): void {
      const target = event.target
      if (!(target instanceof Node)) return
      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const triggerLabel = isViewingSelf
    ? 'Viewing as: yourself'
    : `Viewing as: ${displayName(impersonated)} · ${roleLabel(impersonated.role)}${
        impersonated.teamName ? ` · ${impersonated.teamName}` : ''
      }`

  return (
    <div
      ref={containerRef}
      data-testid="viewing-as-bar"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="viewing-as-trigger"
        title={triggerLabel}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 36,
          maxWidth: 280,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-2)',
          background: isViewingSelf
            ? 'var(--surface-2)'
            : 'var(--warning-bg)',
          border: `1px solid ${
            isViewingSelf ? 'var(--line-strong)' : 'var(--warning-line)'
          }`,
          borderRadius: 999,
          cursor: 'pointer',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            background: isViewingSelf ? 'var(--text-3)' : 'var(--warning)',
          }}
        />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {triggerLabel}
        </span>
      </button>

      {!isViewingSelf ? (
        <button
          type="button"
          onClick={clear}
          data-testid="viewing-as-exit"
          aria-label="Exit viewing as"
          style={{
            minHeight: 36,
            padding: '6px 10px',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-2)',
            background: 'transparent',
            border: '1px solid var(--line-strong)',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          Exit
        </button>
      ) : null}

      {open ? (
        <EmployeePicker
          onPick={(emp) => {
            setImpersonated(emp)
            setOpen(false)
          }}
          onClear={() => {
            clear()
            setOpen(false)
          }}
          current={impersonated}
        />
      ) : null}
    </div>
  )
}

interface EmployeePickerProps {
  onPick: (emp: ImpersonatedEmployee) => void
  onClear: () => void
  current: ImpersonatedEmployee | null
}

function EmployeePicker({ onPick, onClear, current }: EmployeePickerProps): JSX.Element {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 200)

  const teamsQuery = useQuery({
    queryKey: ['shell', 'teams'],
    queryFn: () => listTeams(),
    staleTime: 5 * 60_000,
  })

  const employeesQuery = useQuery({
    queryKey: ['shell', 'employees-search', debouncedSearch],
    queryFn: () => {
      const query: Parameters<typeof listEmployeesPage>[0] = {
        active: true,
        limit: 20,
        page: 1,
      }
      if (debouncedSearch) query.search = debouncedSearch
      return listEmployeesPage(query)
    },
    staleTime: 30_000,
  })

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const team of teamsQuery.data ?? []) {
      map.set(team.id, team.name)
    }
    return map
  }, [teamsQuery.data])

  const results = employeesQuery.data?.data ?? []

  return (
    <div
      role="listbox"
      data-testid="viewing-as-picker"
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 360,
        overflow: 'auto',
        background: 'var(--surface)',
        border: '1px solid var(--line-strong)',
        borderRadius: 10,
        boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        padding: 8,
        zIndex: 30,
      }}
    >
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or email…"
        autoFocus
        aria-label="Search employees"
        style={{
          width: '100%',
          minHeight: 44,
          padding: '10px 12px',
          fontSize: 13,
          border: '1px solid var(--border)',
          borderRadius: 8,
          marginBottom: 8,
        }}
      />
      {current ? (
        <button
          type="button"
          onClick={onClear}
          style={{
            width: '100%',
            textAlign: 'left',
            padding: '8px 10px',
            fontSize: 12,
            color: 'var(--text-2)',
            background: 'transparent',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          ↩ Reset to yourself
        </button>
      ) : null}
      {employeesQuery.isLoading ? (
        <div style={{ padding: 8, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
      ) : results.length === 0 ? (
        <div style={{ padding: 8, fontSize: 12, color: 'var(--text-3)' }}>
          {debouncedSearch ? 'No employees match that search.' : 'Start typing a name.'}
        </div>
      ) : (
        <ul role="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {results.map((emp) => (
            <li key={emp.id}>
              <button
                type="button"
                role="option"
                aria-selected={current?.id === emp.id}
                onClick={() =>
                  onPick({
                    id: emp.id,
                    name: emp.name,
                    preferredName: emp.preferredName ?? null,
                    email: emp.email,
                    role: emp.role,
                    teamId: emp.teamId,
                    teamName: teamNameById.get(emp.teamId) ?? null,
                  })
                }
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  fontSize: 13,
                  color: 'var(--text)',
                  background:
                    current?.id === emp.id ? 'var(--surface-2)' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <span style={{ fontWeight: 600 }}>{displayName(emp)}</span>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {roleLabel(emp.role)} · {teamNameById.get(emp.teamId) ?? '—'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
